import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";

dayjs.extend(utc);
dayjs.extend(duration);

export const DATE_TIME_FORMATS = {
  TIME_12_HOUR: "h:mm A", // 3:00 AM
  TIME_24_HOUR: "HH:mm", // 15:30
  FULL_DATE_TIME: "MMMM D, YYYY h:mm A", // January 23, 2025 3:30 PM
  SHORT_DATE: "YYYY-MM-DD", // 2025-01-23
  LONG_DATE: "dddd, MMMM D, YYYY", // Thursday, January 23, 2025
  DOT_DATE: "DD.MM.YYYY", // 07.05.2025
  DATETIME_24_HOUR: "YYYY-MM-DD HH:mm", // e.g., 2025-07-02 09:07
} as const;

export const DATE_FORMATS_BY_LOCAL: Record<string, string> = {
  en: "YYYY-MM-DD",
  it: "DD/MM/YYYY",
  fr: "DD/MM/YYYY",
  de: "DD.MM.YYYY",
};

type DayjsInput = string | number | Date | Dayjs | null | undefined;

export const normalizeTimestampToMs = (ts: DayjsInput): number | string | null | undefined => {
  if (ts === null || ts === undefined) return null;
  const numericTs = Number(ts);
  if (!Number.isFinite(numericTs)) return ts; // fall back to original value (likely ISO string)
  // If the value looks like seconds (10 digits) convert to milliseconds for dayjs
  return numericTs < 1e12 ? numericTs * 1000 : numericTs;
};

export const getDateFormatByLocal = (locale: string | null | undefined): string => {
  if (!locale) return DATE_FORMATS_BY_LOCAL.en;

  // Extract language part (e.g., "it" from "it-IT")
  const language = locale.split("-")[0];

  return DATE_FORMATS_BY_LOCAL[language] ?? DATE_FORMATS_BY_LOCAL.en;
};

export const timeAgo = (ts: DayjsInput, now: DayjsInput): string => {
  const currentTime = dayjs(now); // now passed in externally
  const timestamp = dayjs.utc(normalizeTimestampToMs(ts) as number).local(); // ts is in UTC, convert to local

  // Return fallback for invalid or missing timestamps to avoid NaN output
  if (!timestamp.isValid() || !currentTime.isValid()) {
    return "--";
  }

  const diffSeconds = Math.max(currentTime.diff(timestamp, "second"), 0); // avoid negative

  let formattedTime: string;

  if (diffSeconds < 60) {
    formattedTime = `${diffSeconds}s`;
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    formattedTime = `${minutes}min ${seconds}s`;
  } else if (diffSeconds < 86400) {
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    formattedTime = `${hours}h ${minutes}min`;
  } else {
    const days = Math.floor(diffSeconds / 86400);
    const hours = Math.floor((diffSeconds % 86400) / 3600);
    formattedTime = `${days}d ${hours}h`;
  }

  return formattedTime;
};

export const timeAgoEpoch = (ts: DayjsInput): string => {
  const now = dayjs().utc(); // Convert to UTC
  const timestamp = dayjs(normalizeTimestampToMs(ts) as number).utc(); // Parse as UTC
  const diffSeconds = Math.max(now.diff(timestamp, "second"), 0);

  let formattedTime: string;

  if (diffSeconds < 60) {
    formattedTime = `${diffSeconds}s`;
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    formattedTime = `${minutes}min ${seconds}s`;
  } else if (diffSeconds < 86400) {
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    formattedTime = `${hours}h ${minutes}min`;
  } else {
    const days = Math.floor(diffSeconds / 86400);
    const hours = Math.floor((diffSeconds % 86400) / 3600);
    formattedTime = `${days}d ${hours}h`;
  }

  return formattedTime;
};

// Format Function
export const formatDateTime = (
  date: DayjsInput,
  format: string = DATE_TIME_FORMATS.SHORT_DATE
): string => {
  return dayjs(date).format(format);
};

export const getTimeAgo = (ts: DayjsInput): string | null => {
  if (ts !== null && ts !== undefined && ts !== "") {
    const now = dayjs();
    const timestamp = dayjs(ts);
    const diffSeconds = now.diff(timestamp, "second");

    let formattedTime: string;

    if (diffSeconds < 60) {
      // Less than a minute → show seconds
      formattedTime = `${diffSeconds}s`;
    } else if (diffSeconds < 3600) {
      // Less than an hour → show minutes and seconds
      const minutes = Math.floor(diffSeconds / 60);
      const seconds = diffSeconds % 60;
      formattedTime = `${minutes}min ${seconds}s`;
    } else if (diffSeconds < 86400) {
      // Less than a day → show hours and minutes
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      formattedTime = `${hours}h ${minutes}min`;
    } else {
      // More than a day → show days and hours
      const days = Math.floor(diffSeconds / 86400);
      const hours = Math.floor((diffSeconds % 86400) / 3600);
      formattedTime = `${days}d ${hours}h`;
    }
    return formattedTime;
  }
  return null;
};

export const getTimeDifference = (
  startTimestamp: DayjsInput,
  endTimestamp: DayjsInput,
  selectedDate: DayjsInput
): string => {
  if (!startTimestamp || !endTimestamp) return "0h 0min";

  const start = dayjs(startTimestamp);

  const isToday = dayjs(selectedDate).isSame(dayjs(), "day");

  const end = endTimestamp
    ? dayjs(endTimestamp)
    : isToday
      ? dayjs() // use current time if selectedDate is today
      : dayjs(selectedDate).endOf("day"); // else use end of the selected date

  const diffSeconds = end.diff(start, "second");

  let formattedTime: string;

  if (diffSeconds < 60) {
    formattedTime = `${diffSeconds}s`;
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    formattedTime = `${minutes}min ${seconds}s`;
  } else if (diffSeconds < 86400) {
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    formattedTime = `${hours}h ${minutes}min`;
  } else {
    const days = Math.floor(diffSeconds / 86400);
    const hours = Math.floor((diffSeconds % 86400) / 3600);
    formattedTime = `${days}d ${hours}h`;
  }

  return formattedTime;
};

export const formatTimeRange = (
  startTimestamp: string,
  endTimestamp: string,
  selectedDate: DayjsInput | DayjsInput[]
): string => {
  if (!startTimestamp || !endTimestamp) return `00:00 - 00:00`;

  const start = startTimestamp.endsWith("Z")
    ? dayjs.utc(startTimestamp).local().format("HH:mm")
    : dayjs(startTimestamp).format("HH:mm");

  const selected =
    Array.isArray(selectedDate) && selectedDate.length > 0 ? selectedDate[0] : selectedDate;

  const isToday = dayjs(selected).isSame(dayjs(), "day");

  const end = endTimestamp
    ? endTimestamp.endsWith("Z")
      ? dayjs.utc(endTimestamp).local().format("HH:mm")
      : dayjs(endTimestamp).format("HH:mm")
    : isToday
      ? dayjs().format("HH:mm")
      : dayjs(selected).endOf("day").format("HH:mm");

  return `${start} - ${end}`;
};

// Format ISO date to readable string
export const formatDate = (dateString: DayjsInput, format = "YYYY-MM-DD HH:mm"): string => {
  return dayjs(dateString).format(format);
};

// Calculate time difference in minutes
export const getTimeDifferenceMinutes = (
  startTime: DayjsInput,
  endTime: Date | DayjsInput = new Date()
): number => {
  return dayjs(endTime).diff(dayjs(startTime), "minute");
};

// Convert minutes to hours
export const minutesToHours = (minutes: number): number => minutes / 60;

// Format duration in minutes to "Hh Mm"
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours ? hours + "h " : ""}${mins}m`;
};

// Get minutes since last update
export const getMinutesSinceUpdate = (updateTime: DayjsInput): number => {
  return dayjs().diff(dayjs(updateTime), "minute");
};

// Format to DD/MM/YYYY (e.g., 14/02/2026)
export const formatDayMonthYear = (date: DayjsInput): string => {
  if (!date) return "";
  return dayjs(date).format("DD/MM/YYYY");
};

export const getNowUtcTime = (): string => {
  return dayjs.utc().format();
};

export const getWeekReferenceDates = (
  timestamp: number = Date.now()
): { currentWeekStartDate: string; twoWeeksAgoStartDate: string } => {
  const current = dayjs(timestamp).startOf("day");
  const daysFromMonday = (current.day() + 6) % 7;
  const currentWeekMonday = current.subtract(daysFromMonday, "day");
  const twoWeeksAgoMonday = currentWeekMonday.subtract(7, "day");

  return {
    currentWeekStartDate: currentWeekMonday.format("DD/MM/YY"),
    twoWeeksAgoStartDate: twoWeeksAgoMonday.format("DD/MM/YY"),
  };
};

/** Translation function type (e.g. from i18next) */
export type TFunction = (key: string, options?: Record<string, number | string>) => string;

/**
 * Format notification time according to specific rules
 * @param timestamp - Unix timestamp in seconds
 * @param t - Translation function from i18next
 * @param locale - Current locale (e.g., 'en', 'it')
 * @returns Formatted time string
 */
export const formatNotificationTime = (
  timestamp: number | null | undefined,
  t: TFunction,
  locale = "en"
): string => {
  if (!timestamp) return "";

  const now = dayjs();
  const notificationTime = dayjs(normalizeTimestampToMs(timestamp) as number);

  if (!notificationTime.isValid()) return "";

  const diffMinutes = now.diff(notificationTime, "minute");
  const diffHours = now.diff(notificationTime, "hour");
  const diffDays = now.diff(notificationTime, "day");

  // <1m → now (adesso)
  if (diffMinutes < 1) {
    return t("time_now");
  }

  // < 1h → minutes ago (minuti fa)
  if (diffMinutes < 60) {
    return t("time_minutes_ago", { count: diffMinutes });
  }

  // 1h → 1 hour ago (1 ora fa)
  if (diffHours === 1) {
    return t("time_one_hour_ago");
  }

  // < 24h → hours ago (ore fa)
  if (diffHours < 24) {
    return t("time_hours_ago", { count: diffHours });
  }

  // yesterday → yesterday (ieri)
  if (diffDays === 1) {
    return t("time_yesterday");
  }

  // < 7 days → X days ago (X giorni fa)
  if (diffDays < 7) {
    return t("time_days_ago", { count: diffDays });
  }

  // ≥ 7 days → insert the date with dateformat depending the locale
  const dateFormat = getDateFormatByLocal(locale);
  return notificationTime.format(dateFormat);
};
